import React from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import { Avatar, Box, Button, IconButton, List, ListItemButton, Stack, Typography } from '@mui/material'

import { TravellerListItemVm } from '@/src/hooks/travellers/useAccountTravellers'
import { TravellersEmptyState } from '@/src/components/desktop/accounts/travellers/travellers'

interface MobileTravellersViewProps {
    t: (key: string) => string
    travellers: TravellerListItemVm[]
    onSelectTraveller: (id: number) => void
    onBack: () => void
    isRtl: boolean
}

const MobileTravellersView: React.FC<MobileTravellersViewProps> = ({ t, travellers, onSelectTraveller, onBack, isRtl }) => {
    return (
        <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', position: 'relative' }}
            >
                <IconButton aria-label={t('title')} onClick={onBack} sx={{ ml: -1 }}>
                    <ArrowBackIcon sx={{ fontSize: 20, transform: isRtl ? 'scaleX(-1)' : 'none' }} />
                </IconButton>
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, fontSize: '16px', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
                >
                    {t('title')}
                </Typography>
                <Button
                    variant="outlined"
                    disabled
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 14, borderRadius: 2, px: 1, py: 0.5 }}
                >
                    {t('add')}
                </Button>
            </Stack>

            <Box sx={{ p: 2 }}>
                {travellers.length === 0 ? (
                    <TravellersEmptyState t={t} showPageTitle={false} />
                ) : (
                    <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {travellers.map(item => (
                            <ListItemButton
                                key={item.id}
                                onClick={() => onSelectTraveller(item.id)}
                                sx={{
                                    minHeight: 72,
                                    justifyContent: 'space-between',
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: '10px',
                                    p: 2
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: 'grey.200',
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                width: 36,
                                                height: 36,
                                                fontSize: '14px'
                                            }}
                                        >
                                            {item.initials || <PersonOutlinedIcon sx={{ fontSize: 14 }} />}
                                        </Avatar>

                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                lineHeight: '20px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {item.displayName}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" spacing={0.75}>
                                        <IconButton disabled aria-label={t('edit')} sx={{ width: 32, height: 32 }}>
                                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <Box sx={{ width: '1px', height: '16px', bgcolor: 'divider', alignSelf: 'center' }} />
                                        <IconButton disabled aria-label={t('delete')} sx={{ width: 32, height: 32 }}>
                                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    )
}

export { MobileTravellersView }
export default MobileTravellersView
export type { MobileTravellersViewProps }
